<template>
  <Teleport to="#header">
    <Header v-once />
  </Teleport>

  <div
    v-if="pageDataLoaded"
    class="grid-container"
    :style="[hasRightContainer && orderSummaryElement ? `minHeight:${rightContainerHeight}px` : '']"
  >
    <TechnicalError
      v-if="hasTechnicalError"
      :error-thrown="technicalErrorThrown"
      @close="onTechnicalOverlayClosed"
    />

    <div
      class="grid-x grid-margin-x margin-top-s"
      :class="{ 'containing-block': !(emptyBag && bagError) }"
    >
      <LazyLoad
        v-if="bagError"
        @viewportRenderingFinished="viewportRenderingFinished"
      >
        <ErrorPage
          id="errorPage"
          class="cell"
        />
      </LazyLoad>

      <LazyLoad
        v-else-if="emptyBag"
        @viewportRenderingFinished="viewportRenderingFinished"
      >
        <EmptyBag
          id="emptyBag"
          class="cell"
          :class="{ invisible: !dataRevalidated }"
        />
        <SaveForLater
          v-if="showSaveForLater && dataRevalidated && (saveForLaterItems.length || sflLoadError)"
          id="saveForLater"
          key="saveForLater"
          class="cell"
        />
        <Pros
          v-if="dataRevalidated"
          key="pros"
          class="cell"
        />
      </LazyLoad>

      <template v-else>
        <div class="cell small-12 large-7">
          <BagHeader
            id="bagHeader"
            :class="isBMSMEligible && !errorDisplayingBMSMContainer && 'bag-separator'"
          />
          <template v-if="!internationalMode">
            <div
              v-if="showRoktCCBannerTop"
              class="rokt-cc-banner-relative-wrapper"
            >
              <div
                v-if="!fallbackNadCCBannerTop"
                class="rokt-cc-banner-container"
              >
                <Skeleton
                  v-if="!hasRoktCCBannerTopContent"
                  width="100%"
                  height="100%"
                />
                <div
                  id="rokt-cc-banner-top"
                  ref="roktCCBannerTop"
                />
              </div>
              <div
                v-if="renderFallbackNadCCBannerTop"
                ref="fallbackNadCCBannerTopWrapper"
                :class="{ 'nad-cc-banner-hidden': !fallbackNadCCBannerTop }"
              >
                <CreditCardBanner
                  :credit-new-account-discount-enabled="creditNewAccountDiscountEnabled"
                  :card-activation-enhancements-enabled="cardActivationEnhancementsAndBagEnabled"
                  :credit-new-account-dynamic-banner="creditNewAccountDynamicBanner"
                  :credit-new-account-dynamic-congrats-banners-enabled="creditNewAccountDynamicCongratsBannersEnabled"
                  is-congratulatory-banner-eligible
                  source="shopping-bag"
                  bringback-page="ShoppingBag"
                  credit-acquisition="ShoppingBag"
                  v-bind="newDiscountApiBagEnabled ? { 'credit-banner-discount-payload': creditBannerDiscountPayload } : {}"
                  :is-page-killswitch-active="newDiscountApiBagEnabled"
                  :is-prequal-nad-experiment-enabled="isPrequalNadExperimentEnabled"
                />
              </div>
            </div>
            <CreditCardBanner
              v-else-if="!isBMSMEligible || errorDisplayingBMSMContainer"
              :credit-new-account-discount-enabled="creditNewAccountDiscountEnabled"
              :card-activation-enhancements-enabled="cardActivationEnhancementsAndBagEnabled"
              :credit-new-account-dynamic-banner="creditNewAccountDynamicBanner"
              :credit-new-account-dynamic-congrats-banners-enabled="creditNewAccountDynamicCongratsBannersEnabled"
              is-congratulatory-banner-eligible
              source="shopping-bag"
              bringback-page="ShoppingBag"
              credit-acquisition="ShoppingBag"
              v-bind="newDiscountApiBagEnabled ? { 'credit-banner-discount-payload': creditBannerDiscountPayload } : {}"
              :is-page-killswitch-active="newDiscountApiBagEnabled"
              :is-prequal-nad-experiment-enabled="isPrequalNadExperimentEnabled"
            />
            <NTOHeader />
          </template>
          <BMSMContainer
            v-if="isBMSMEligible"
            source="bag"
            :buy-more-save-more-data="buyMoreSaveMoreData"
            class="padding-bottom-s bag-separator"
            @force-hide-bmsm-container="() => errorDisplayingBMSMContainer = true"
          />
          <BagItems id="bagitems" />

          <LazyLoad>
            <BagGWPItems
              v-if="hasGWPItems"
              id="gwpItems"
            />
            <StarRewards
              v-if="showStarRewards"
              id="starRewards"
            />
            <SaveForLater
              v-if="showSaveForLater && newSFLRedesignEnabled && (saveForLaterItems.length || sflLoadError)"
              id="saveForLater"
              key="saveForLater"
            />
          </LazyLoad>
        </div>

        <!--Order Summary also includes the CC banner. The CC banner is displayed when the BMSM tracker is eligible and the BMSM component is loaded.
          Any changes to the CC banner must be implemented in both the app and the Order Summary.-->
        <div class="cell small-12 large-4 large-offset-1 right-container">
          <Teleport to="body">
            <template v-if="showStickyCXOButton">
              <PaymentButtons
                id="bag-sticky-CXO"
                class="show-for-small-only"
                :is-m-e-w-sticky-c-x-o="showStickyCXOButton"
              />
            </template>
          </Teleport>
          <LazyLoad :sequential="!hasRightContainer">
            <OrderSummary
              id="orderSummary"
              ref="orderSummary"
              class="full-ht"
              :is-prequal-nad-experiment-enabled="isPrequalNadExperimentEnabled"
              :show-cta-banner-within-order-summary="isBMSMEligible && !errorDisplayingBMSMContainer"
              :credit-new-account-discount-enabled="creditNewAccountDiscountEnabled"
              :card-activation-enhancements-enabled="cardActivationEnhancementsAndBagEnabled"
              :credit-banner-discount-payload="creditBannerDiscountPayload"
              :is-page-killswitch-active="newDiscountApiBagEnabled"
              :is-rokt-c-c-banner-enabled="isRoktCCBannerEnabled"
              :has-right-container="hasRightContainer"
              :rokt-c-c-banner-props-top="roktCCBannerPropsTop"
              :user-has-card-from-session="userHasCardFromSession"
              :is-b-m-s-m-eligible="isBMSMEligible"
              @vue:mounted="setOrderSummaryHeight"
              @vue:updated="setOrderSummaryHeight"
            />
          </LazyLoad>
        </div>

        <div class="cell small-12 large-7">
          <LazyLoad
            sequential
            @viewportRenderingFinished="viewportRenderingFinished"
          >
            <PromotionOffers
              v-if="hasPromotionOffers && !newSFLRedesignEnabled"
              id="promotionOffers"
            />
            <Pros key="pros" />
            <SaveForLater
              v-if="showSaveForLater && !newSFLRedesignEnabled && (saveForLaterItems.length || sflLoadError)"
              id="saveForLater"
              key="saveForLater"
            />
            <PromotionOffers
              v-if="hasPromotionOffers && newSFLRedesignEnabled"
              id="promotionOffers"
            />
          </LazyLoad>
        </div>
      </template>
    </div>
  </div>

  <Teleport to="#footer">
    <div
      v-show="aboveTheFoldRendered"
      id="teleports"
      class="margin-top-xxxl"
      :class="showStickyCXOButton && !bagError && !emptyBag && 'sticky-CXO-footer'"
    />
  </Teleport>
</template>

<script setup>
import {
  computed, onBeforeMount, onErrorCaptured, onMounted, ref, inject, onUnmounted, watch,
} from 'vue';
import { useStore } from 'vuex';
import Skeleton from '@atomic-ui/skeleton';
import { useResizeObserver, useSessionStorage } from '@vueuse/core';
import breakpoints from '@common-vue/atomic-ui/src/components/picture/breakpoints.module.scss';
import {
  FETCH_SFL_DATA, MODULE_NAMESPACE, SHOW_SFL, SHOW_STAR_REWARDS, UPDATE_PICKUP_STORE,
  usePageViewAnalytics, SHOW_GLITCH_OVERLAY, HIDE_GLITCH_OVERLAY, IS_PAYPAL_BUTTON_ENABLED,
  CREDIT_DISCOUNT_BANNER_ORIGIN, CREDIT_DISCOUNT_BANNER_ITEMS, IS_ROKT_ENABLED, loadRoktScript, GET_BMSM_DATA,
  IS_ROKT_SDK_PLUS_ENABLED,
} from '@common-vue/my-bag-consumable';
import { SET_COOKIE_DATA } from '@common-vue/bag-checkout-common-consumable/src/types/mutations';
import BagHeader from '@common-vue/my-bag-consumable/components/BagHeader';
import BagItems from '@common-vue/my-bag-consumable/components/BagItems';
import EmptyBag from '@common-vue/my-bag-consumable/components/EmptyBag';
import NTOHeader from '@common-vue/my-bag-consumable/components/NTOHeader';
import { loadApplePaySdk } from '@common-vue/my-bag-consumable/utils/loadApplePaySdk';
import asyncComponent from '@common-vue/bag-checkout-common-consumable/src/utils/asyncComponent';
import CreditCardBanner from '@common-vue/creditcard-banner-consumable';
import BMSMContainer from '@common-vue/buy-more-save-more';
import setCookiesWithSameSiteForPayment from '@common-vue/bag-checkout-common-consumable/src/utils/setCookiesWithSameSiteForPayment';
import { pageStoreChangeListener } from '@common-vue/locator/src/utils/globalStoreChange';
import useLoadUtag from '@common-vue/bag-checkout-common-consumable/src/composables/useLoadUtag';
import loadScripts from '@common-vue/paypal-consumable/src/utils/loadScripts';
import getOptanonConsent, { removeConsentListener } from '@utils/roktSDKPlus/roktConsent';
import { SET_INITIAL_PAGE_DATA } from '@types/actions';
import { fetchPageData } from '@services/PageDataService';
import AsyncDataHandler from '@utils/AsyncDataHandler';
import Header from '@components/Header';
import LazyLoad from '@components/LazyLoadComponent';
import fireExperimentTag from '@utils/fireExperimentTag';

let sflDataHandler = new AsyncDataHandler();
const isLargeDevice = !!(typeof window !== 'undefined' && window.matchMedia?.(`(min-width: ${breakpoints.large})`).matches);

defineProps({
  pageDataLoaded: Boolean,
  dataRevalidated: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['viewMounted', 'viewRendered', 'forceHideBmsmContainer']); // 'viewRendered' is triggered after above the fold elements are done rendering

// Data
const aboveTheFoldRendered = ref(false);
const hasRightContainer = ref(isLargeDevice);
const orderSummary = ref(null);
const orderSummaryElement = computed(() => orderSummary.value?.$el);
const rightContainerHeight = ref(0);
const errorDisplayingBMSMContainer = ref(false);
const isRoktSDKPlusInitialized = ref(false);
const sflLoadError = ref(false);

const store = useStore();
const cookies = inject('cookies');
const logger = inject('logger');
const hasTechnicalError = computed(() => store.state[MODULE_NAMESPACE].hasTechnicalError);
const technicalErrorThrown = computed(() => store.state[MODULE_NAMESPACE].technicalErrorThrown);
const isPaypalEnabled = computed(() => store.getters[`${MODULE_NAMESPACE}/${IS_PAYPAL_BUTTON_ENABLED}`]);
const signedIn = computed(() => store.state.signedIn);
const loagUtag = useLoadUtag();
const { firePageViewTag } = usePageViewAnalytics();

const setOrderSummaryHeight = () => {
  const el = orderSummaryElement.value;
  if (hasRightContainer.value && el) {
    el.style.height = 'auto'; // temporarily setting height to auto, to override height:100% from class full-ht, otherwise rightContainerHeight is calculated wrong.
    rightContainerHeight.value = el.scrollHeight; // rightContainerHeight is used to reset scrollable height on every update in order summary section.
    el.style.height = ''; // resetting height so that height:auto doesn't override the height 100% of class full-ht .
  }
};

// Computed properties
const internationalMode = computed(() => store.state.internationalMode);
const isRoktEnabled = computed(() => store.getters[`${MODULE_NAMESPACE}/${IS_ROKT_ENABLED}`]);
const showStickyCXOButton = computed(() => store.state.killswitches.MEWStickyCXOEnabled);

const creditNewAccountDiscountEnabled = computed(() => store.state.killswitches.creditNewAccountDiscountEnabled);
const cardActivationEnhancementsEnabled = computed(() => store.state.killswitches.cardActivationEnhancementsEnabled);
const cardActivationEnhancementsBagEnabled = computed(() => store.state.killswitches.cardActivationEnhancementsBagEnabled);
const cardActivationEnhancementsAndBagEnabled = computed(() => cardActivationEnhancementsEnabled.value && cardActivationEnhancementsBagEnabled.value);
const newDiscountApiBagEnabled = computed(() => store.state.killswitches.newDiscountApiBagEnabled);
const isPrequalNadExperimentEnabled = computed(() => store.state.killswitches.isPrequalNadExperimentEnabled);
const newSFLRedesignEnabled = computed(() => store.state.killswitches.newSFLRedesignEnabled);
const creditNewAccountDynamicBanner = computed(() => store.state.killswitches.creditNewAccountDynamicBanner);
const creditNewAccountDynamicCongratsBannersEnabled = computed(() => store.state.killswitches.creditNewAccountDynamicCongratsBanners);
const state = store.state[MODULE_NAMESPACE];
// mapState
const bagError = computed(() => state.bagError);
const emptyBag = computed(() => !(state.items?.length));
const hasPromotionOffers = computed(() => !!(state.promotionOffersMapped?.length));
const hasGWPItems = computed(() => !!(state.bagGWPItemsMapped?.length));

// mapGetters
const showSaveForLater = computed(() => store.getters[`${MODULE_NAMESPACE}/${SHOW_SFL}`]);
const showStarRewards = computed(() => store.getters[`${MODULE_NAMESPACE}/${SHOW_STAR_REWARDS}`]);
const buyMoreSaveMoreData = computed(() => store.getters[`${MODULE_NAMESPACE}/${GET_BMSM_DATA}`]);
const isRoktSDKPlusEnabled = computed(() => store.getters[`${MODULE_NAMESPACE}/${IS_ROKT_SDK_PLUS_ENABLED}`]);
const isRoktCCBannerEnabled = computed(() => store.state.killswitches.isRoktCCBannerEnabled);
const roktApiKey = computed(() => store.state.envProps?.roktApiKey);
const isBMSMEnabled = computed(() => store.state.killswitches.isBMSMEnabled);
const paypalBraintreeNewSdk = computed(() => store.state.killswitches.paypalBraintreeNewSdk);
const isBMSMEligible = computed(() => isBMSMEnabled.value && buyMoreSaveMoreData.value.length);
const saveForLaterItems = computed(() => store.state[MODULE_NAMESPACE].saveForLaterItemsMapped || []);
const isApplePayEnabledForAllBrowsers = computed(() => store.state.killswitches?.isApplePayEnabledForAllBrowsers);
const sourceApp = 'Shopping Bag';
const macysCardInProfile = computed(() => store.state[MODULE_NAMESPACE].starRewardsDetails?.macysCardInProfile);

const creditCardApplySession = useSessionStorage('creditCardApply', {});
const userHasCardFromSession = computed(() => creditCardApplySession.value?.userHasCard === true);

const showRoktCCBannerTop = computed(() => (
  isRoktCCBannerEnabled.value
  && hasRightContainer.value
  && !isBMSMEligible.value
  && !macysCardInProfile.value
  && !userHasCardFromSession.value
));

const roktCCBannerTop = ref(null);
const hasRoktCCBannerTopContent = ref(false);

useResizeObserver(roktCCBannerTop, () => {
  const el = roktCCBannerTop.value;
  if (!el) return;
  hasRoktCCBannerTopContent.value = el.clientHeight > 0;
});

const fallbackNadCCBannerTop = ref(false);
const renderFallbackNadCCBannerTop = ref(false);
const roktCCBannerFallbackTimeout = computed(() => store.state.envProps?.roktCCBannerFallbackTimeout ?? 6000);
const fallbackNadCCBannerTopWrapper = ref(null);
let roktCCBannerTopFallbackTimer = null;
let fallbackNadCCBannerTopReadyTimer = null;

const clearRoktTimeouts = () => {
  if (roktCCBannerTopFallbackTimer) clearTimeout(roktCCBannerTopFallbackTimer);
  if (fallbackNadCCBannerTopReadyTimer) clearTimeout(fallbackNadCCBannerTopReadyTimer);
  roktCCBannerTopFallbackTimer = null;
  fallbackNadCCBannerTopReadyTimer = null;
};

useResizeObserver(fallbackNadCCBannerTopWrapper, (entries) => {
  const el = entries[0]?.target;
  // Swap ONLY when the CC banner API has finished and successfully rendered its DOM (>10px height)
  if (el && el.clientHeight > 10 && renderFallbackNadCCBannerTop.value && !fallbackNadCCBannerTop.value) {
    fallbackNadCCBannerTop.value = true;
    if (fallbackNadCCBannerTopReadyTimer) {
      clearTimeout(fallbackNadCCBannerTopReadyTimer);
      fallbackNadCCBannerTopReadyTimer = null;
    }
  }
});

const triggerFallbackNadCCBannerTop = () => {
  if (!hasRoktCCBannerTopContent.value && !renderFallbackNadCCBannerTop.value) {
    renderFallbackNadCCBannerTop.value = true;
    fallbackNadCCBannerTopReadyTimer = setTimeout(() => {
      fallbackNadCCBannerTop.value = true;
    }, 2000);
  }
};

watch(hasRoktCCBannerTopContent, (hasContent) => {
  if (hasContent) {
    clearRoktTimeouts();
    if (renderFallbackNadCCBannerTop.value && !fallbackNadCCBannerTop.value) {
      renderFallbackNadCCBannerTop.value = false;
    }
  }
});

watch(showRoktCCBannerTop, (showRokt) => {
  if (showRokt) {
    fallbackNadCCBannerTop.value = false;
    renderFallbackNadCCBannerTop.value = false;
    clearRoktTimeouts();
    roktCCBannerTopFallbackTimer = setTimeout(triggerFallbackNadCCBannerTop, roktCCBannerFallbackTimeout.value);
  } else {
    hasRoktCCBannerTopContent.value = false;
    clearRoktTimeouts();
  }
}, { immediate: true });

// Payload
const creditBannerDiscountPayload = computed(() => ({
  originInfo: store.getters[`${MODULE_NAMESPACE}/${CREDIT_DISCOUNT_BANNER_ORIGIN}`],
  promotions: store.state[MODULE_NAMESPACE].bagPromotions || {},
  items: store.getters[`${MODULE_NAMESPACE}/${CREDIT_DISCOUNT_BANNER_ITEMS}`],
  subTotal: store.state[MODULE_NAMESPACE].priceSummary?.subtotal || {},
}));

const roktCCBannerPropsTop = computed(() => ({
  creditBannerDiscountPayload: creditBannerDiscountPayload.value,
  cardActivationEnhancementsEnabled: cardActivationEnhancementsAndBagEnabled.value,
  creditNewAccountDynamicBanner: creditNewAccountDynamicBanner.value,
  source: 'shopping-bag',
  bringbackPage: 'ShoppingBag',
  creditAcquisition: 'ShoppingBag',
  isPageKillswitchActive: newDiscountApiBagEnabled.value,
  creditNewAccountDiscountEnabled: creditNewAccountDiscountEnabled.value,
  isPrequalNadExperimentEnabled: isPrequalNadExperimentEnabled.value,
}));

// methods
function fetchSFLData({ isDataRevalidated } = {}) {
  // If data is being revalidated , then __timeNow__ should not be used in url - hence passing overrideCache as true.
  // If data is being fetched first time , then __timeNow__ should be used along with Url - hence passing overrideCache as false.
  // logic to add or not add __timeNow__  exists in my-bag-consumable actions.js/BagService files under FETCH_SFL_DATA action
  return store.dispatch(`${MODULE_NAMESPACE}/${FETCH_SFL_DATA}`, { overrideCache: isDataRevalidated });
}

async function loadHeaderFooter() {
  try {
    const scriptSrc = `/navigation/nuxt/header-footer?csrJS=true&_timeNow_=${window._timeNow_}`;
    const script = document.createElement('script');
    script.src = scriptSrc;
    /*
        https://web.dev/speed-script-loading/#the-dom-to-the-rescue
        async=false will load the files in an order without blocking the rendering
      */
    script.async = false;
    document.head.appendChild(script);
  } catch (error) {
    logger.error('Error while loading headerfooter', error);
  }
}

function viewportRenderingFinished() {
  /*
      When user removes the last item and <EmptyBag> is rendered, then we do not want the following code to execute it again
    */
  if (!aboveTheFoldRendered.value) {
    aboveTheFoldRendered.value = true;
    emit('viewRendered');
    // Don't allow header footer files to interfere with the bag related files
    setTimeout(loadHeaderFooter);
  }
}

function importOrderSummary() {
  return new Promise((resolve) => {
    // loadScripts on the load or ordersummary is called to improve the loading of the paypal braintree button on the page load.
    /* keeping it behind the check - isPaypalEnabled.value.
        Same checks are used to initialize/render paypalBraintree button in my-bag-consumable.
    */
    /*
     * Defer loading of PayPal scripts when the Sticky CXO button is enabled.
     * The PaymentButtons component will handle initializing them when rendered to optimize initial page load.
     * Note: Same checks are used to initialize/render the paypalBraintree button in my-bag-consumable.
    */
    if (isPaypalEnabled.value) {
      const paypalBraintreeSdkVersion = store.state.envProps?.paypalBraintreeSdkVersion;

      loadScripts(paypalBraintreeSdkVersion, paypalBraintreeNewSdk.value);
    }
    if (isRoktEnabled.value) {
      try {
        loadRoktScript();
      } catch (e) {
        logger.error('ROKT - Error while loading ROKT script - ', e);
      }
    }

    if ((isRoktSDKPlusEnabled.value || isRoktCCBannerEnabled.value) && !internationalMode.value && !window.mParticle?.Rokt) {
      getOptanonConsent().then(async (val) => {
        if (val) {
          const { default: loadRoktSDKPlusScript } = await import(/* webpackPreload: true */ '@utils/roktSDKPlus/loadRoktSDKPlusScript');
          await loadRoktSDKPlusScript({ brand: process.env.ENTRY_POINT, apiKey: roktApiKey.value, screenName: 'bag' });
          isRoktSDKPlusInitialized.value = true;
          window.dispatchEvent(new CustomEvent('roktsdkplusready', { detail: { initialized: true } }));
        } else {
          throw new Error('no consent received before loading ROKT script');
        }
      }).catch((e) => {
        isRoktSDKPlusInitialized.value = false;
        logger.error('ROKT - error loading script - ', e);
      });
    }

    if (isApplePayEnabledForAllBrowsers.value && window && !window.ApplePaySession) {
      try {
        loadApplePaySdk();
      } catch (error) {
        // loadApplePaySdk may not throw error since its not getting awaited
        // This is added just in case there are some synchronous errors
        logger.error('Error while preloading Apple Pay SDK from bag.vue', error);
      }
    }

    resolve(import(/* webpackChunkName: "OrderSummary", webpackPrefetch: true */ '@common-vue/my-bag-consumable/components/OrderSummary'), {
      mandatory: true,
      showErrorOverlay: true,
      sourceApp,
      sectionName: 'Order Summary',
    });
  });
}

// Async components
const BagGWPItems = asyncComponent(() => import(/* webpackChunkName: "BagGWPItems", webpackPrefetch: true */ '@common-vue/my-bag-consumable/components/BagGWPItems'), {
  mandatory: true, showErrorOverlay: true, sourceApp, sectionName: 'Free Gifts',
});
const ErrorPage = asyncComponent(() => import(/* webpackChunkName: "ErrorPage", webpackPrefetch: true */ '@common-vue/my-bag-consumable/components/ErrorPage'), {
  mandatory: true,
  showErrorOverlay: true,
  sourceApp,
  sectionName: 'Error Page',
});
const OrderSummary = asyncComponent(importOrderSummary);
const PromotionOffers = asyncComponent(() => import(/* webpackChunkName: "PromotionOffers", webpackPrefetch: true */ '@common-vue/my-bag-consumable/components/promotionOffers/PromotionOffers'), {
  sourceApp,
  sectionName: 'Bonus Offers',
});
const Pros = asyncComponent(() => import(/* webpackChunkName: "Pros", webpackPrefetch: true */ '@common-vue/my-bag-consumable/components/Pros'), { sourceApp, sectionName: 'Recommended or recently viewed products' });
const SaveForLater = asyncComponent(async (preCache = false) => {
  try {
    const [Component] = await Promise.all([
      newSFLRedesignEnabled.value ? import(/* webpackChunkName: "SaveForLater", webpackPreload: true */ '@common-vue/my-bag-consumable/components/SaveForLaterCarousel') : import(/* webpackChunkName: "SaveForLater", webpackPrefetch: true */ '@common-vue/my-bag-consumable/components/SaveForLater'),
      sflDataHandler && sflDataHandler.hasData(),
    ]);

    return Component;
  } finally {
    if (!preCache) {
      sflDataHandler = null;
    }
  }
  /**
   * Save For Later is loaded only for SignedIn and Soft SigneIn users.
   * For Soft SignedIn - making SaveForLater as Optional (User won't see error message but error will be logged/Tagged)
   *    Refer - @common-vue/bag-checkout-common-consumable/src/components/ErrorComponent/index.vue
   * For SignedIn - making it mandatory
   */
}, { mandatory: !newSFLRedesignEnabled.value && signedIn.value, sourceApp, sectionName: 'Save For Later' });
const StarRewards = asyncComponent(() => import(/* webpackChunkName: "StarRewards", webpackPrefetch: true */ '@common-vue/my-bag-consumable/components/StarRewards'), {
  sourceApp,
  sectionName: 'Star Rewards',
});
const TechnicalError = asyncComponent(
  () => import(/* webpackChunkName: "TechnicalError", webpackPrefetch: true */ '@common-vue/my-bag-consumable/components/overlays/TechnicalError'), {
    lazy: false,
    loadingComponent: null,
    sourceApp,
    sectionName: 'Technical Overlay',
  }, // not participating in the LazyLoader's sequencing logic
);

const PaymentButtons = asyncComponent(() => import(/* webpackChunkName: "PaymentButtons", webpackPreload: true */ '@common-vue/my-bag-consumable/components/OrderSummary/PaymentButtons'), {
  lazy: false,
  sourceApp,
  sectionName: 'MEW Sticky CXO Buttons',
});

function openTechnicalErrorOverlay(error) {
  store.commit(`${MODULE_NAMESPACE}/${SHOW_GLITCH_OVERLAY}`, error);
}

function onTechnicalOverlayClosed() {
  store.commit(`${MODULE_NAMESPACE}/${HIDE_GLITCH_OVERLAY}`);
}
// callback for updateNavigationIntContext event
// refresh complete page data
async function handleNavigationIntContextUpdate(event) {
  try {
    const { pageName, dataUrl } = store.state.pageData;
    const pageDataApiTimeout = store.state.envProps?.pageDataApiTimeout;

    // TODO: LOW - some of the logic below is used in App.vue too , refctor this code to avoid duplicate code
    // Adding loader here as we need loader for two scenarios 1. when data is loaded from cache  2. also when data is fetched from API
    // At this point app is not aware if data will be loaded from cache or from API
    store.commit('showLoader');

    // Make Bag API Call through mybag
    // This will fetch data from cache if service worker is running and data is already cached
    // If data is not cached already then fresh data is returned from API
    const { data = {}, headers } = await fetchPageData({ dataUrl, pageDataApiTimeout });

    // If data is loaded from cache (Behind the scene fresh data is retrieved from API)
    if (headers?.get('X-Is-Validating') === 'true') {
      // Let store know that data is fetched from cache
      store.commit('dataRevalidationState', { value: false });
    }

    const dataRevalidated = computed(() => store.state.revalidationState?.value);
    // If data is loaded from cache
    if (dataRevalidated.value === false) {
      // Add a watcher to dataRevalidated - this will help to know when data is feteched from API behind the scene
      const unwatchDataRevalidated = watch(() => store.state.revalidationState?.value, (revalidated) => {
        // when fresh data is retrieved from API
        if (revalidated) {
          // Reset store details with latest cookies ( Reference file - @common-vue/bag-checkout-common-consumable/src/store.js)
          store.commit(SET_COOKIE_DATA, cookies);
          // Remove the watcher
          unwatchDataRevalidated();
          // Remove Loader
          store.commit('hideLoader');
          // log the info
          logger.info('Data revalidated and UI refreshed with latest data from API after user has changed international context to:', event?.detail);
        }
      });
    } else {
      // refresh data in UI when data is fetched from API
      store.dispatch(SET_INITIAL_PAGE_DATA, { pageName, data });
      // Reset store details with latest cookies ( Reference file - @common-vue/bag-checkout-common-consumable/src/store.js)
      store.commit(SET_COOKIE_DATA, cookies);
      // Hide loader
      store.commit('hideLoader');
      // log the info
      logger.info('Data loaded from API and UI refreshed with latest data after user has changed international context to:', event?.detail);
    }
  } catch (error) {
    logger.error('Error while refreshing data after user has changed international context to:', event?.detail, error);
    // If any error occurs , hide the loader
    store.commit('hideLoader');
  }
}

onErrorCaptured((/* error, instance, info */ error) => {
  // Adding to ignore the error by Modal Overlay component
  // TODO: HIGH - TEC-2795 Remove this code when atomic-ui is done with fixes for ModalOverlay
  if (error?.name === 'InvalidStateError') {
    logger.error('InvalidStateError is thrown from component', error);
    // returning false here to prevent the error from propagating further
    // Invalid state error is thrown only in specific browsers for Modal Overlay
    // We dont want to stop user from processing further since error thrown is because of missed code
    return false;
  }
  // Open Technical error overlay to inform user about error
  openTechnicalErrorOverlay(error);
  // Let the error propagate further
  return true;
});

async function fireViewTagAndExperimentTag() {
  /**
   * Utag's 'view' tag and EXPSDK's fireAdobeTags should happen in sequence
   * Hence we are awaiting for utag library to load and then firing both tags
   */
  await loagUtag();
  // Fire 'view' tag for Analytics
  firePageViewTag({ immediate: true });
  // Fire 'Experimentation' tag
  fireExperimentTag();
}
/**
 * This will get called in two scenarios
 * 1. When fresh data is loaded ( data is not loaded from cache )
 * 2. When data is revalidated
 *
 * This won't get called with data is loaded from cache
 *
 * @param { Object } { isDataRevalidated } - represents if data is loaded from cache
 * isDataRevalidated - true -> Data is revalidated after its loaded from cache
 * isDataRevalidated - false -> Fresh data is loaded without cache (At this time, above the fold DOM should have been rendered already)
 */
function onReady({ isDataRevalidated } = {}) {
  /**
   * Fire 'view' tag and 'experiment' tag
   * TODO: MEDIUM tagging here may further slow down for scenario where userId, userGuid, bagGuid from cookies
   *  is different than the one in response, analyse fix for this (USERID_GUID_BAGGUID_DIFFERENT)
   */
  fireViewTagAndExperimentTag();

  // if data is revalidated, then make SFL call
  if (showSaveForLater.value && isDataRevalidated) {
    if (sflLoadError.value) {
      sflLoadError.value = false;
    }
    fetchSFLData({ isDataRevalidated }).catch(() => {
      sflLoadError.value = true;
    });
  }

  /**
   * PROS needs be rendered forcefully even if it is out of viewport
   * OnReady is called only after viewport is rendered in case of fresh API data
   * OnReady is called only after data is revalidated in case of data already being cached
   *  (In this case, initially, above viewPort is rendered with cached data but PROS needs productIds from latest bag data,
   *     hence calling renderForcefully here for PROS )
  */
  setTimeout(Pros.renderForcefully, 500);
}
/**
 * onBeforeMount will get called only when page loads first and won't get called on revalidation
 */
onBeforeMount(async () => {
  cookies.set('FORWARDPAGE_KEY', '/my/bag');
  if (showSaveForLater.value) {
    try {
      await fetchSFLData();
      if (sflDataHandler) {
        sflDataHandler.resolve();
      }
    } catch (err) {
      sflLoadError.value = true;
      if (sflDataHandler) {
        sflDataHandler.reject(err);
      }
    }
  }
});
onMounted(() => {
  // Reset same site value for cookies which were set for express paypal during paypal checkout
  // Here passing true -> set original same site for all cookies set when express paypal is clicked
  setCookiesWithSameSiteForPayment(true);
  // Bag needs to listen to store change event from header,
  // This listener has to added only once, hence it is present in OnMounted
  // pageStoreChangeListener is event trigger from header
  pageStoreChangeListener(async ({ detail: storeDetails } = {}) => {
    const locationNumber = storeDetails?.newStore?.locationNumber;
    if (locationNumber) {
      // Make API Call
      await store.dispatch(`${MODULE_NAMESPACE}/${UPDATE_PICKUP_STORE}`, { locationNumber });
    }
  });
  emit('viewMounted');

  // updateNavigationIntContext is a event triggered from InternationalContext when user changes
  // country or currency from International context slideout
  // @common-vue/international-context/src/components/EntryForm.vue
  window.addEventListener('updateNavigationIntContext', handleNavigationIntContextUpdate);

  if (roktCCBannerTop.value) {
    roktCCBannerTop.value.innerHTML = '';
  }
});

onUnmounted(() => {
  // Remove listener anytime this component is unmounted (which 99% wont happen if user stays in bag)
  window.removeEventListener('updateNavigationIntContext', handleNavigationIntContextUpdate);
  removeConsentListener();
  clearRoktTimeouts();
});

defineExpose({ // eslint-disable-line no-undef
  onReady,
});

</script>

<style src="appSCSS" lang='scss'></style>
<style src="./css/global.scss" lang="scss"></style>
<style src="./css/mcom.scss" brand="mcom" lang="scss"></style>
<style src="./css/common.scss" lang="scss" scoped></style>
<style src="./css/roktccbanner.scss" lang="scss" scoped></style>
